import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardHeader, CardTitle, Input, Spinner } from "reactstrap";
import { IoAddOutline, IoCalendarOutline, IoTimeOutline } from "react-icons/io5";
import { get } from "../../../../../../utils/helpers/api";
import { useSettings } from "../../../../../../context/settings";
import { printLeaveType } from "../../../../utils/helpers";

/** Stable accent per leave type, so a type keeps its colour across employees. */
const TYPE_COLORS = {
  'annual-leave': '#6C63FF',
  'sick-leave': '#0ea5e9',
  'authorized-leave': '#f59e0b',
  'unauthorized-leave': '#ef4444',
  'long-term-sick-leave': '#0284c7',
  'maternity-leave': '#ec4899',
  'paternity-leave': '#8b5cf6',
  'marriage-leave': '#14b8a6',
  'bereavement-leave': '#64748b',
  'unpaid-leave': '#94a3b8',
  'training-study-leave': '#22c55e'
};

const STATUS_CLASSES = {
  approved: 'ok',
  pending: 'pending',
  rejected: 'rejected',
  cancelled: 'cancelled'
};

const typeColor = (type) => TYPE_COLORS[type] || '#6C63FF';

function BalanceCard({ balance, t }) {
  const color = typeColor(balance.leave_type);
  const hasEntitlement = balance.entitlement != null;
  const percent = hasEntitlement && balance.entitlement > 0
    ? Math.min(100, Math.round((balance.used / balance.entitlement) * 100))
    : 0;

  return (
    <div className="emp-leave-card">
      <div className="type">
        <span className="swatch" style={{ background: color }} /> {printLeaveType(balance.leave_type)}
      </div>

      {hasEntitlement ?
        <>
          <div className="emp-leave-nums">
            <span className="used">{balance.remaining}</span>
            <span className="total">/ {balance.entitlement} {t('hr.days').toLowerCase()}</span>
          </div>
          <div className="emp-progress">
            <span style={{ width: percent + "%", background: color }} />
          </div>
          <div className="emp-leave-foot">
            {t('hr.daysUsedPercent', { used: balance.used, percent })}
            {balance.pending > 0 && ` · ${t('hr.daysPending', { count: balance.pending })}`}
          </div>
        </>
      :
        <>
          <div className="emp-leave-nums">
            <span className="used">{balance.used}</span>
            <span className="total">{t('hr.days').toLowerCase()}</span>
          </div>
          <div className="emp-leave-foot">
            {t('hr.noEntitlement')}
            {balance.pending > 0 && ` · ${t('hr.daysPending', { count: balance.pending })}`}
          </div>
        </>
      }
    </div>
  );
}

function LeaveTab({ employeeId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!employeeId) return;

    let cancelled = false;

    const getLeaveSummary = async () => {
      setLoading(true);

      const data = await get(`/hr/employees/${employeeId}/leave?year=${year}`);

      if (cancelled) return;

      if (data?.success) {
        setSummary(data);
        setError(false);
      } else {
        setError(true);
      }

      setLoading(false);
    };

    getLeaveSummary();

    return () => { cancelled = true; };
  }, [employeeId, year]);

  if (loading && !summary) {
    return <Spinner color="primary" className="d-block mx-auto my-4" />;
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <div className="emp-empty">{t('hr.failedLoadLeave')}</div>
        </CardBody>
      </Card>
    );
  }

  const years = summary?.available_years?.length ? summary.available_years : [year];

  return (
    <div>
      <Card className="mb-3">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <CardTitle>
            <IoCalendarOutline size={18} /> {t('hr.leaveBalance')}
          </CardTitle>
          <div className="d-flex align-items-center gap-2">
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 100 }}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map(option => <option key={option} value={option}>{option}</option>)}
            </Input>
            <button
              type="button"
              className="emp-link-btn"
              onClick={() => navigate('/hr/leave-requests/create')}
            >
              <IoAddOutline size={14} /> {t('hr.requestLeave')}
            </button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="emp-leave-summary">
            <div>
              <div className="emp-field-label">{t('hr.totalTaken')}</div>
              <div className="emp-leave-stat">{summary?.summary?.used ?? 0}</div>
            </div>
            <div>
              <div className="emp-field-label">{t('hr.totalPending')}</div>
              <div className="emp-leave-stat">{summary?.summary?.pending ?? 0}</div>
            </div>
            <div>
              <div className="emp-field-label">{t('hr.totalRequests')}</div>
              <div className="emp-leave-stat">{summary?.summary?.requests ?? 0}</div>
            </div>
          </div>

          {summary?.balances?.length ?
            <div className="emp-leave-cards">
              {summary.balances.map(balance => (
                <BalanceCard key={balance.leave_type} balance={balance} t={t} />
              ))}
            </div>
          :
            <div className="emp-empty">{t('hr.noLeaveBalance')}</div>
          }
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><IoTimeOutline size={18} /> {t('hr.leaveHistory')}</CardTitle>
        </CardHeader>
        <CardBody style={{ padding: 6 }}>
          {summary?.requests?.length ?
            <table className="emp-table">
              <thead>
                <tr>
                  <th>{t('hr.leaveType')}</th>
                  <th>{t('hr.dates')}</th>
                  <th>{t('hr.totalDays')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('hr.decidedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.requests.map(request => (
                  <tr key={request.id}>
                    <td style={{ fontWeight: 600 }}>
                      <span
                        className="swatch me-2"
                        style={{
                          background: typeColor(request.leave_type?.split(',')[0]?.trim()),
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 3
                        }}
                      />
                      {printLeaveType(request.leave_type)}
                    </td>
                    <td>
                      {request.start_date === request.end_date
                        ? formatDate(request.start_date)
                        : `${formatDate(request.start_date)} – ${formatDate(request.end_date)}`}
                    </td>
                    <td>{request.days_in_year}</td>
                    <td>
                      <span className={"emp-badge " + (STATUS_CLASSES[request.status] || '')}>
                        {t(`common.${request.status === 'cancelled' ? 'canceled' : request.status}`)}
                      </span>
                    </td>
                    <td>{request.decided_by || request.requested_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          :
            <div className="emp-empty">{t('hr.noLeaveRequests')}</div>
          }
        </CardBody>
      </Card>
    </div>
  );
}

export default LeaveTab;
